import { Question, QuestionFactory, Serializer } from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "singleradio"; // Use lowercase for consistency

// QUESTION MODEL
class QuestionSingleRadioModel extends Question {
  constructor(name) {
    super(name);
    // Set default values
    this.setPropertyValue("value", null);
    this.setPropertyValue("radioLabel", "Option"); // Custom label property
  }

  getType() {
    return QUESTION_TYPE;
  }

  // Simple getter/setter for value
  get value() {
    return this.getPropertyValue("value");
  }

  set value(val) {
    // Convert to boolean: true, false, or null
    let booleanVal = null;
    if (val === true || val === "true" || val === 1) {
      booleanVal = true;
    } else if (val === false || val === "false" || val === 0) {
      booleanVal = false;
    }
    this.setPropertyValue("value", booleanVal);
  }

  // Custom property for radio button label
  get radioLabel() {
    return this.getPropertyValue("radioLabel", "Option");
  }

  set radioLabel(val) {
    this.setPropertyValue("radioLabel", val);
  }

  // Override title to use radioLabel when empty
  get title() {
    const customTitle = super.title;
    // If no title is set, use radioLabel
    if (!customTitle || customTitle.trim() === "") {
      return this.radioLabel;
    }
    return customTitle;
  }

  set title(val) {
    super.title = val;
  }
}

// REACT COMPONENT
const SingleRadioComponent = (props) => {
  const question = props.question;

  if (!question) {
    return (
      <div style={{ padding: "10px", color: "#999" }}>Loading question...</div>
    );
  }

  const isChecked = question.value === true;

  // Use radioLabel if available, otherwise fallback to title
  const labelText =
    question.radioLabel || question.title || question.name || "Option";

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "4px 0",
      }}
    >
      <input
        type="radio"
        checked={isChecked}
        onChange={() => (question.value = !isChecked)}
        disabled={question.isReadOnly}
        style={{
          width: "16px",
          height: "16px",
          margin: 0,
          cursor: question.isReadOnly ? "not-allowed" : "pointer",
        }}
      />
      <span
        style={{
          fontSize: "14px",
          color: question.isReadOnly ? "#6c757d" : "#212529",
          cursor: question.isReadOnly ? "not-allowed" : "pointer",
        }}
      >
        {labelText}
      </span>
    </div>
  );
};

// REGISTRATION FUNCTION
export function registerSingleRadioQuestion() {
  console.log(`Registering question type: ${QUESTION_TYPE}`);

  // Check if already registered
  const existingTypes = Array.from(QuestionFactory.Instance.getAllTypes());
  if (existingTypes.includes(QUESTION_TYPE)) {
    console.log(`${QUESTION_TYPE} is already registered, skipping...`);
    return;
  }

  try {
    // 1. Register with Serializer - ADD radioLabel PROPERTY
    Serializer.addClass(
      QUESTION_TYPE,
      [
        {
          name: "radioLabel",
          type: "text",
          category: "general",
          displayName: "Radio Button Label",
          default: "Option",
          visible: true,
          isRequired: false,
          onSetValue: function (obj, value) {
            obj.setPropertyValue("radioLabel", value);
          },
          onGetValue: function (obj) {
            return obj.getPropertyValue("radioLabel");
          },
        },
        {
          name: "defaultValue:boolean",
          category: "general",
          displayName: "Default Checked",
          default: false,
          visible: true,
        },
      ],
      function () {
        return new QuestionSingleRadioModel("");
      },
      "question" // Base class
    );

    // Hide the standard "title" property if you want only radioLabel
    // Or keep both - users can use either
    setTimeout(() => {
      const titleProp = Serializer.findProperty(QUESTION_TYPE, "title");
      if (titleProp) {
        titleProp.visible = true; // Keep visible if you want both
        titleProp.category = "general";
      }

      // Hide unnecessary properties
      const hideProps = ["description", "commentText", "requiredErrorText"];
      hideProps.forEach((propName) => {
        const prop = Serializer.findProperty(QUESTION_TYPE, propName);
        if (prop) prop.visible = false;
      });
    }, 0);

    // 2. Register with QuestionFactory
    QuestionFactory.Instance.registerQuestion(QUESTION_TYPE, function (name) {
      return new QuestionSingleRadioModel(name);
    });

    // 3. Register React component
    ReactQuestionFactory.Instance.registerQuestion(
      QUESTION_TYPE,
      function (props) {
        return <SingleRadioComponent {...props} />;
      }
    );

    console.log(`Successfully registered ${QUESTION_TYPE}`);
  } catch (error) {
    console.error(`Failed to register ${QUESTION_TYPE}:`, error);
  }
}

export default QuestionSingleRadioModel;